import React, { useEffect, useMemo, useState } from "react";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import MasterV2 from "../MasterV2";

import Swal from "sweetalert2";

import { LoadingDiv, SweetAlertShow } from "../../master/Master";

import { courseList } from "../../../api/Courses";
import { batchList } from "../../../api/Batch";
import { StaffList } from "../../../api/Staff";

import {
  studentAttendanceList,
  studentAttendancePost,
  studentManualAttendancePost,
  studentAttendanceBatchMarkPost,
} from "../../../api/Attendance";

import { formatToTime } from "../../master/FormatToDateTime";

/* =========================================================
   HELPERS
========================================================= */

const getToday = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentTime = () => {
  const date = new Date();

  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
};

const getInitials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const safeTime = (value) => {
  if (!value) return "-";

  try {
    return formatToTime(value);
  } catch {
    return value;
  }
};

/* =========================================================
   MESSAGE STATUS

   Existing convention from your old code:
   1 = Sent
   3 = Processing
   4 = Failed
   otherwise = Not Sent
========================================================= */

const getMessageStatus = (value) => {
  const status = Number(value);

  if (status === 1) {
    return {
      key: "sent",
      label: "Sent",
      icon: "ri-checkbox-circle-line",
      className: "message-sent",
    };
  }

  if (status === 3) {
    return {
      key: "processing",
      label: "Processing",
      icon: "ri-loader-4-line",
      className: "message-processing",
    };
  }

  if (status === 4) {
    return {
      key: "failed",
      label: "Failed",
      icon: "ri-error-warning-line",
      className: "message-failed",
    };
  }

  return {
    key: "not_sent",
    label: "Not Sent",
    icon: "ri-close-circle-line",
    className: "message-not-sent",
  };
};

/* =========================================================
   COMPONENT
========================================================= */

export default function StudentAttendanceManualv2() {
  /* =======================================================
     STATE
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [filterLoading, setFilterLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [courses, setCourses] = useState([]);

  const [batches, setBatches] = useState([]);

  const [staffs, setStaffs] = useState([]);

  const [sessions, setSessions] = useState([]);

  const [data, setData] = useState({});

  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const [attendanceType, setAttendanceType] = useState("present_absent");

  const [punchStudent, setPunchStudent] = useState(null);

  const [punchTime, setPunchTime] = useState("");

  const [messageStudent, setMessageStudent] = useState(null);

  const [messageReportOpen, setMessageReportOpen] = useState(false);

  const [messageReportFilter, setMessageReportFilter] = useState("");

  const [filters, setFilters] = useState({
    session_id: "",
    course_id: "",
    batch_id: "",
    staff_id: "",
    date: getToday(),
  });

  /* =======================================================
     SESSIONS
  ======================================================= */

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("sessions") ?? "[]");

      setSessions(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      console.error("Session parse error:", error);

      setSessions([]);
    }
  }, []);

  useEffect(() => {
    if (!sessions.length) {
      return;
    }

    const activeSession = sessions.find(
      (session) => Number(session?.status) === 1,
    );

    if (!activeSession?.id) {
      return;
    }

    setFilters((previous) => {
      if (previous.session_id) {
        return previous;
      }

      return {
        ...previous,
        session_id: String(activeSession.id),
      };
    });
  }, [sessions]);

  /* =======================================================
     COURSE
  ======================================================= */

  const getCourses = async () => {
    try {
      setFilterLoading(true);

      const response = await courseList();

      if (response?.data?.status) {
        setCourses(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error("Course error:", error);

      setCourses([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load courses.",
      );
    } finally {
      setFilterLoading(false);
    }
  };

  /* =======================================================
     STAFF
  ======================================================= */

  const getStaffs = async () => {
    try {
      const response = await StaffList("status=1");

      if (response?.data?.status) {
        setStaffs(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setStaffs([]);
      }
    } catch (error) {
      console.error("Staff error:", error);

      setStaffs([]);
    }
  };

  useEffect(() => {
    getCourses();
    getStaffs();
  }, []);

  /* =======================================================
     BATCHES

     IMPORTANT:
     Course is optional here.

     This means:
     Session selected
       -> all session batches

     Course selected
       -> only course batches
  ======================================================= */

  const getBatches = async () => {
    if (!filters.session_id) {
      setBatches([]);
      return;
    }

    try {
      setFilterLoading(true);

      let addUrl = "&status=1";

      addUrl += `&session_id=${encodeURIComponent(filters.session_id)}`;

      if (filters.course_id) {
        addUrl += `&course_id=${encodeURIComponent(filters.course_id)}`;
      }

      const response = await batchList(addUrl);

      if (response?.data?.status) {
        setBatches(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Batch error:", error);

      setBatches([]);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    getBatches();

    // eslint-disable-next-line
  }, [filters.session_id, filters.course_id]);

  /* =======================================================
     BUILD ATTENDANCE QUERY
  ======================================================= */

  const buildAttendanceQuery = () => {
    const query = {
      session_id: filters.session_id,

      course_id: filters.course_id,

      batch_id: filters.batch_id,

      staff_id: filters.staff_id,

      date: filters.date,
    };

    let addUrl = "";

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== "") {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    return addUrl;
  };

  /* =======================================================
     GET ATTENDANCE LIST

     IMPORTANT:
     NO BATCH = NO STUDENT FETCH
  ======================================================= */

  const getStudentAttendanceList = async () => {
    if (!filters.batch_id) {
      setData({});
      return;
    }

    if (!filters.session_id || !filters.date) {
      setData({});
      return;
    }

    try {
      setLoading(true);

      const addUrl = buildAttendanceQuery();

      const response = await studentAttendanceList(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        setData({});

        if (response?.data?.message) {
          SweetAlertShow("info", "Information", response.data.message);
        }
      }
    } catch (error) {
      console.error("Attendance error:", error);

      setData({});

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load attendance.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     FETCH ONLY WHEN BATCH SELECTED
  ======================================================= */

  useEffect(() => {
    if (!filters.batch_id) {
      setData({});
      return;
    }

    getStudentAttendanceList();

    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.date,
  ]);

  /* =======================================================
     RAW ROWS
  ======================================================= */

  const rows = Array.isArray(data?.data) ? data.data : [];

  const totalCount = Number(data?.total ?? rows.length ?? 0);

  const countPresent = Number(data?.present ?? 0);

  const countAbsent = Number(data?.absent ?? 0);

  const countNotMarked = Number(data?.no_mark ?? data?.not_marked ?? 0);

  /* =======================================================
     ATTENDANCE STATUS
  ======================================================= */

  const getAttendanceStatus = (item) => {
    const attendance = item?.today_attendance;

    const checkIn = Number(attendance?.check_in);

    if (checkIn === 1) {
      return {
        key: "present",
        filter: "1",
        label: "Present",
        className: "attendance-present",
        icon: "ri-checkbox-circle-line",
      };
    }

    if (checkIn === 2) {
      return {
        key: "absent",
        filter: "2",
        label: "Absent",
        className: "attendance-absent",
        icon: "ri-close-circle-line",
      };
    }

    return {
      key: "not_marked",
      filter: "0",
      label: "Not Marked",
      className: "attendance-not-marked",
      icon: "ri-time-line",
    };
  };

  /* =======================================================
     SEARCH + STATUS FILTER
  ======================================================= */

  const filteredRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return rows.filter((item) => {
      const status = getAttendanceStatus(item);

      if (statusFilter && status.filter !== statusFilter) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      const course = item?.latest_course_and_fee?.course?.title || "";

      const batch = item?.latest_course_and_fee?.batch?.title || "";

      const searchable = [
        item?.name,
        item?.registration_no,
        item?.phone,
        item?.mobile,
        course,
        batch,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(keyword);
    });
  }, [rows, search, statusFilter]);

  /* =======================================================
     SELECTED FILTER OBJECTS
  ======================================================= */

  const selectedSession = useMemo(
    () =>
      sessions.find((item) => String(item?.id) === String(filters.session_id)),
    [sessions, filters.session_id],
  );

  const selectedCourse = useMemo(
    () =>
      courses.find((item) => String(item?.id) === String(filters.course_id)),
    [courses, filters.course_id],
  );

  const selectedBatch = useMemo(
    () => batches.find((item) => String(item?.id) === String(filters.batch_id)),
    [batches, filters.batch_id],
  );

  const selectedStaff = useMemo(
    () => staffs.find((item) => String(item?.id) === String(filters.staff_id)),
    [staffs, filters.staff_id],
  );

  /* =======================================================
     UPDATE SINGLE ATTENDANCE
  ======================================================= */

  const updateAttendance = async (registrationNo, type) => {
    if (!registrationNo) {
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("registration_no", registrationNo);

      formData.append("type", type);

      formData.append("date", filters.date);

      const response = await studentAttendancePost(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Attendance Updated",
          response?.data?.message || "Attendance updated successfully.",
        );

        await getStudentAttendanceList();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Attendance could not be updated.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to update attendance.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     MARK ALL NOT MARKED -> ABSENT
  ======================================================= */

  const handleMarkAllAbsent = async () => {
    if (!filters.batch_id) {
      SweetAlertShow("info", "Select Batch", "Please select a batch first.");

      return;
    }

    if (countNotMarked <= 0) {
      SweetAlertShow(
        "info",
        "Nothing Pending",
        "All students already have attendance marked.",
      );

      return;
    }

    const result = await Swal.fire({
      icon: "warning",

      title: "Mark All Pending As Absent?",

      html: `
            <div style="font-size:14px;line-height:1.6;">
              <strong>${countNotMarked}</strong>
              student(s) are currently not marked.
              <br />
              They will all be marked
              <strong style="color:#d14343;">Absent</strong>
              for
              <strong>${escapeHtml(filters.date)}</strong>.
            </div>
          `,

      showCancelButton: true,

      confirmButtonText: "Yes, Mark Absent",

      cancelButtonText: "Cancel",

      confirmButtonColor: "#d14343",

      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("session_id", filters.session_id);

      formData.append("batch_id", filters.batch_id);

      formData.append("date", filters.date);

      if (filters.course_id) {
        formData.append("course_id", filters.course_id);
      }

      if (filters.staff_id) {
        formData.append("staff_id", filters.staff_id);
      }

      const response = await studentAttendanceBatchMarkPost(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Attendance Updated",
          response?.data?.message ||
          `${countNotMarked} student(s) marked absent.`,
        );

        setStatusFilter("");

        await getStudentAttendanceList();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No attendance was updated.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to mark attendance.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     ATTENDANCE MODE
  ======================================================= */

  const changeAttendanceType = async (type) => {
    if (type === "in_out" && attendanceType !== "in_out") {
      const result = await Swal.fire({
        icon: "info",

        title: "Switch to IN / OUT?",

        text: "Manual IN / OUT attendance can also trigger enabled attendance messages.",

        showCancelButton: true,

        confirmButtonText: "Continue",

        cancelButtonText: "Cancel",

        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setAttendanceType(type);
    setPunchStudent(null);
    setPunchTime("");
  };

  /* =======================================================
     OPEN PUNCH
  ======================================================= */

  const openPunchModal = (item) => {
    setPunchStudent(item);

    setPunchTime(getCurrentTime());
  };

  /* =======================================================
     PUNCH IN / OUT

     send_message = 1 is preserved from old page.
  ======================================================= */

  const handlePunch = async () => {
    if (!punchStudent?.registration_no || !punchTime) {
      SweetAlertShow("error", "Required", "Please select punch time.");

      return;
    }

    try {
      setSaving(true);

      const response = await studentManualAttendancePost({
        registration_no: punchStudent.registration_no,

        date: filters.date,

        time: punchTime,

        send_message: "1",
      });

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Attendance Updated",
          response?.data?.message || "Attendance marked successfully.",
        );

        setPunchStudent(null);
        setPunchTime("");

        await getStudentAttendanceList();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Attendance could not be marked.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to mark attendance.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     MESSAGE HELPERS
  ======================================================= */

  const getStudentMessageData = (item) => {
    const attendance = item?.today_attendance || {};

    const numberStatus = (value) => {
      const parsed = Number(value);
      return [1, 2, 3, 4].includes(parsed) ? parsed : 2;
    };

    const makeChannel = (key, label, icon, rawStatus) => {
      const status = numberStatus(rawStatus);

      return {
        key,
        label,
        icon,
        rawStatus: status,
        status: getMessageStatus(status),
        delivered: status === 1,
        notSent: status === 2,
        processing: status === 3,
        failed: status === 4,
      };
    };

    const makeEvent = (key, label, time, metaStatus, pushStatus) => {
      const meta = makeChannel(
        "meta",
        "WhatsApp / Meta",
        "ri-whatsapp-line",
        metaStatus,
      );

      const push = makeChannel(
        "push",
        "Push Notification",
        "ri-notification-3-line",
        pushStatus,
      );

      const deliveredChannels = [meta, push].filter(
        (channel) => channel.delivered,
      );

      const hasDelivered = deliveredChannels.length > 0;

      const hasProcessing = meta.processing || push.processing;

      const hasFailed = meta.failed || push.failed;

      return {
        key,
        label,
        time,
        meta,
        push,
        deliveredChannels,
        hasDelivered,
        hasProcessing,
        hasFailed,
        deliveryLabel: hasDelivered
          ? "Delivered"
          : hasProcessing
            ? "Processing"
            : hasFailed
              ? "Failed"
              : "Not Delivered",
      };
    };

    /*
     * IMPORTANT FIELD MAPPING
     *
     * Check IN:
     *   WhatsApp/Meta -> in_message_status
     *   Push          -> push_in_message_status
     *
     * Check OUT:
     *   WhatsApp/Meta -> out_message_status
     *   Push          -> push_out_message_status
     *
     * General attendance/manual message:
     *   WhatsApp/Meta -> message_status
     *   Push          -> push_message_status
     */
    const inEvent = makeEvent(
      "in",
      "Check IN",
      attendance?.check_in_time,
      attendance?.in_message_status,
      attendance?.push_in_message_status,
    );

    const outEvent = makeEvent(
      "out",
      "Check OUT",
      attendance?.check_out_time,
      attendance?.out_message_status,
      attendance?.push_out_message_status,
    );

    const generalEvent = makeEvent(
      "general",
      "Attendance Message",
      null,
      attendance?.message_status,
      attendance?.push_message_status,
    );

    const history = [];

    if (
      attendance?.check_in_time ||
      attendance?.in_message_status ||
      attendance?.push_in_message_status
    ) {
      history.push(inEvent);
    }

    if (
      attendance?.check_out_time ||
      attendance?.out_message_status ||
      attendance?.push_out_message_status
    ) {
      history.push(outEvent);
    }

    /*
     * General status is kept for Present/Absent/manual attendance
     * messages. For punch attendance we already have exact IN/OUT
     * history, so do not duplicate it unless there is no punch history.
     */
    if (
      history.length === 0 &&
      (attendance?.message_status || attendance?.push_message_status)
    ) {
      history.push(generalEvent);
    }

    const deliveredHistory = history.filter((event) => event.hasDelivered);

    const deliveredChannels = [];

    history.forEach((event) => {
      event.deliveredChannels.forEach((channel) => {
        const alreadyAdded = deliveredChannels.some(
          (existing) => existing.key === channel.key,
        );

        if (!alreadyAdded) {
          deliveredChannels.push(channel);
        }
      });
    });

    const hasDelivered = deliveredHistory.length > 0;

    const hasProcessing = history.some((event) => event.hasProcessing);

    const hasFailed = history.some((event) => event.hasFailed);

    const deliveryLabel = hasDelivered
      ? "Delivered"
      : hasProcessing
        ? "Processing"
        : hasFailed
          ? "Failed"
          : "Not Delivered";

    return {
      // Backward-compatible fields used by existing summary/report code.
      meta: generalEvent.meta.status,
      push: generalEvent.push.status,
      inMessage: inEvent.meta.status,
      outMessage: outEvent.meta.status,

      inEvent,
      outEvent,
      generalEvent,
      history,
      deliveredHistory,
      deliveredChannels,
      hasDelivered,
      hasProcessing,
      hasFailed,
      deliveryLabel,

      raw: {
        in_message_status: numberStatus(attendance?.in_message_status),
        push_in_message_status: numberStatus(
          attendance?.push_in_message_status,
        ),
        out_message_status: numberStatus(attendance?.out_message_status),
        push_out_message_status: numberStatus(
          attendance?.push_out_message_status,
        ),
        message_status: numberStatus(attendance?.message_status),
        push_message_status: numberStatus(attendance?.push_message_status),
      },
    };
  };

  /* =======================================================
     MESSAGE SUMMARY

     This is calculated directly from loaded batch students.
  ======================================================= */

  const messageSummary = useMemo(() => {
    const summary = {
      meta: {
        sent: 0,
        not_sent: 0,
        processing: 0,
        failed: 0,
      },

      push: {
        sent: 0,
        not_sent: 0,
        processing: 0,
        failed: 0,
      },
    };

    rows.forEach((item) => {
      const message = getStudentMessageData(item);

      if (summary.meta[message.meta.key] !== undefined) {
        summary.meta[message.meta.key] += 1;
      }

      if (summary.push[message.push.key] !== undefined) {
        summary.push[message.push.key] += 1;
      }
    });

    return summary;
  }, [rows]);

  /* =======================================================
     MESSAGE REPORT STUDENTS
  ======================================================= */

  const messageReportRows = useMemo(() => {
    if (!messageReportFilter) {
      return rows;
    }

    return rows.filter((item) => {
      const message = getStudentMessageData(item);

      if (messageReportFilter === "meta_not_sent") {
        return message.meta.key === "not_sent" || message.meta.key === "failed";
      }

      if (messageReportFilter === "meta_sent") {
        return message.meta.key === "sent";
      }

      if (messageReportFilter === "push_not_sent") {
        return message.push.key === "not_sent" || message.push.key === "failed";
      }

      if (messageReportFilter === "push_sent") {
        return message.push.key === "sent";
      }

      return true;
    });
  }, [rows, messageReportFilter]);

  /* =======================================================
     OPEN INDIVIDUAL MESSAGE DELIVERY
  ======================================================= */

  const openMessageStudent = (item) => {
    setMessageStudent(item);
  };

  /* =======================================================
     IMPORTANT - RESEND MESSAGE

     Your supplied Attendance API file/page does not contain
     a dedicated resend endpoint.

     DO NOT call studentManualAttendancePost here just to
     resend, because that may alter IN/OUT attendance.

     Once your resend API exists, replace this function.
  ======================================================= */

  const resendAttendanceMessage = () => {
    SweetAlertShow(
      "info",
      "Resend API Required",
      "Message delivery can be viewed here. A dedicated resend attendance-message API is required before retry can safely be enabled.",
    );
  };

  /* =======================================================
     RESET
  ======================================================= */

  const resetFilters = () => {
    const active = sessions.find((session) => Number(session?.status) === 1);

    setData({});
    setSearch("");
    setStatusFilter("");

    setFilters({
      session_id: active?.id ? String(active.id) : "",

      course_id: "",
      batch_id: "",
      staff_id: "",
      date: getToday(),
    });
  };

  /* =======================================================
     PRINT
  ======================================================= */

  const printAttendance = () => {
    if (!filters.batch_id) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch before printing.",
      );

      return;
    }

    if (!filteredRows.length) {
      SweetAlertShow("info", "No Students", "There are no students to print.");

      return;
    }

    const tableRows = filteredRows
      .map((item, index) => {
        const attendance = item?.today_attendance;

        const status = getAttendanceStatus(item);

        const batch =
          item?.latest_course_and_fee?.batch?.title ||
          selectedBatch?.title ||
          "-";

        const course =
          item?.latest_course_and_fee?.course?.title ||
          selectedCourse?.title ||
          "-";

        return `
              <tr>

                <td>
                  ${index + 1}
                </td>

                <td class="student">

                  <strong>
                    ${escapeHtml(item?.name || "-")}
                  </strong>

                  <small>
                    ${escapeHtml(item?.registration_no || "-")}
                  </small>

                </td>

                <td>
                  ${escapeHtml(item?.phone || item?.mobile || "-")}
                </td>

                <td>

                  <strong>
                    ${escapeHtml(batch)}
                  </strong>

                  <br />

                  <small>
                    ${escapeHtml(course)}
                  </small>

                </td>

                <td
                  class="${status.key}"
                >
                  ${escapeHtml(status.label)}
                </td>

                <td>
                  ${escapeHtml(safeTime(attendance?.check_in_time))}
                </td>

                <td>
                  ${escapeHtml(safeTime(attendance?.check_out_time))}
                </td>

              </tr>
            `;
      })
      .join("");

    const printWindow = window.open("", "_blank", "width=1200,height=850");

    if (!printWindow) {
      SweetAlertShow(
        "info",
        "Popup Blocked",
        "Please allow popups and try again.",
      );

      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>

      <head>

        <title>
          Attendance Register
        </title>

        <style>

          @page {
            size: A4 landscape;
            margin: 10mm;
          }

          * {
            box-sizing:
              border-box;
          }

          body {
            margin: 0;

            color: #262b33;

            font-family:
              Arial,
              Helvetica,
              sans-serif;

            font-size: 10px;

            -webkit-print-color-adjust:
              exact;

            print-color-adjust:
              exact;
          }

          .header {
            display: flex;

            justify-content:
              space-between;

            align-items:
              flex-end;

            padding-bottom: 10px;

            margin-bottom: 10px;

            border-bottom:
              2px solid #333;
          }

          h1 {
            margin: 0;

            font-size: 20px;
          }

          .header p {
            margin: 4px 0 0;

            color: #666;
          }

          .meta {
            display: grid;

            grid-template-columns:
              repeat(5, 1fr);

            gap: 6px;

            margin-bottom: 10px;
          }

          .meta div {
            padding: 7px;

            border:
              1px solid #ddd;

            border-radius: 4px;
          }

          .meta span {
            display: block;

            color: #777;

            font-size: 8px;
          }

          .meta strong {
            display: block;

            margin-top: 2px;

            font-size: 10px;
          }

          .summary {
            display: grid;

            grid-template-columns:
              repeat(4,1fr);

            gap: 6px;

            margin-bottom: 10px;
          }

          .summary div {
            padding: 8px;

            border:
              1px solid #ccc;

            text-align: center;
          }

          .summary small {
            display: block;

            color: #777;
          }

          .summary strong {
            display: block;

            margin-top: 3px;

            font-size: 16px;
          }

          table {
            width: 100%;

            border-collapse:
              collapse;
          }

          th,
          td {
            padding: 6px;

            border:
              1px solid #aaa;

            font-size: 9px;
          }

          th {
            background: #eee;

            text-align: center;
          }

          td {
            text-align: center;
          }

          td.student {
            text-align: left;
          }

          .student strong,
          .student small {
            display: block;
          }

          .student small {
            margin-top: 2px;

            color: #777;
          }

          .present {
            color: #167847;

            font-weight: bold;
          }

          .absent {
            color: #c43d3d;

            font-weight: bold;
          }

          .not_marked {
            color: #b47717;

            font-weight: bold;
          }

          .footer {
            display: flex;

            justify-content:
              space-between;

            margin-top: 25px;

            padding-top: 8px;

            border-top:
              1px solid #bbb;

            font-size: 8px;
          }

        </style>

      </head>

      <body>

        <div class="header">

          <div>

            <h1>
              Student Attendance Register
            </h1>

            <p>
              ${escapeHtml(filters.date)}
            </p>

          </div>

          <div>
            Printed:
            ${escapeHtml(new Date().toLocaleString("en-IN"))}
          </div>

        </div>

        <div class="meta">

          <div>
            <span>Session</span>
            <strong>
              ${escapeHtml(selectedSession?.session || "-")}
            </strong>
          </div>

          <div>
            <span>Course</span>
            <strong>
              ${escapeHtml(selectedCourse?.title || "All")}
            </strong>
          </div>

          <div>
            <span>Batch</span>
            <strong>
              ${escapeHtml(selectedBatch?.title || "-")}
            </strong>
          </div>

          <div>
            <span>Teacher</span>
            <strong>
              ${escapeHtml(selectedStaff?.name || "All")}
            </strong>
          </div>

          <div>
            <span>Date</span>
            <strong>
              ${escapeHtml(filters.date)}
            </strong>
          </div>

        </div>

        <div class="summary">

          <div>
            <small>Total Students</small>
            <strong>${totalCount}</strong>
          </div>

          <div>
            <small>Present</small>
            <strong>${countPresent}</strong>
          </div>

          <div>
            <small>Absent</small>
            <strong>${countAbsent}</strong>
          </div>

          <div>
            <small>Not Marked</small>
            <strong>${countNotMarked}</strong>
          </div>

        </div>

        <table>

          <thead>

            <tr>
              <th>#</th>
              <th>Student</th>
              <th>Mobile</th>
              <th>Batch / Course</th>
              <th>Status</th>
              <th>IN</th>
              <th>OUT</th>
            </tr>

          </thead>

          <tbody>
            ${tableRows}
          </tbody>

        </table>

        <div class="footer">

          <span>
            Total Students:
            ${filteredRows.length}
          </span>

          <span>
            Class Teacher Signature
            ______________________

            &nbsp;&nbsp;&nbsp;&nbsp;

            Principal Signature
            ______________________
          </span>

        </div>

        <script>

          window.onload =
            function () {

              setTimeout(
                function () {
                  window.print();
                },
                300
              );

            };

        </script>

      </body>

      </html>
    `);

    printWindow.document.close();
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {(loading || saving) && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard attendance-manual-page">
        <div className="layout-container">
          {/* SIDEBAR / HEADER */}
          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page">
            {/* TOP NAVBAR */}
            <div className="no-print">
              <Nav />
            </div>

            {/* FIXED CONTENT WRAPPER */}
            <div className="content-wrapper ">
              <div className="container-fluid flex-grow-1 container-p-y attendance-manual-container">
                {/* ATTENDANCE MASTER NAVIGATION */}
                <div className="no-print attendance-master-wrapper">
                  <MasterV2 />
                </div>

                <main className="">
                  {/* =============================================
              HEADER
          ============================================= */}

                  {/* <section className="attendance-page-header">
                    <div className="attendance-page-title ">
                      <div className="attendance-title-icon">
                        <i className="ri-calendar-check-line" />
                      </div>

                      <div>
                        <div className="attendance-breadcrumb">
                          Attendance
                          <i className="ri-arrow-right-s-line" />
                          Mark Manually
                        </div>

                        <h1>Mark Student Attendance</h1>

                        <p>
                          Select a batch, review students and mark attendance.
                        </p>
                      </div>
                    </div>

                    <div className="attendance-header-actions">
                      {filters.batch_id && (
                        <button
                          type="button"
                          className="attendance-header-btn message"
                          onClick={() => {
                            setMessageReportFilter("");
                            setMessageReportOpen(true);
                          }}
                        >
                          <i className="ri-message-2-line" />
                          Message Report
                        </button>
                      )}

                      <button
                        type="button"
                        className="attendance-header-btn print"
                        disabled={!filters.batch_id}
                        onClick={printAttendance}
                      >
                        <i className="ri-printer-line" />
                        Print
                      </button>

                      <button
                        type="button"
                        className="attendance-header-btn refresh"
                        disabled={!filters.batch_id}
                        onClick={getStudentAttendanceList}
                      >
                        <i className="ri-refresh-line" />
                        Refresh
                      </button>
                    </div>
                  </section> */}

                  {/* =============================================
              FILTERS
          ============================================= */}

                  <section className="attendance-filter-card">
                    <div className="attendance-filter-header">
                      <div>
                        <h2>
                          <i className="ri-filter-3-line" />
                          Select Students
                        </h2>

                        <p>Select a batch to load students.</p>
                      </div>

                      <div className="attendance-header-actions">
                        <button
                          type="button"
                          className="attendance-reset-btn"
                          onClick={resetFilters}
                        >
                          <i className="ri-restart-line" />
                          Reset
                        </button>

                        {filters.batch_id && (
                          <button
                            type="button"
                            className="attendance-header-btn message"
                            onClick={() => {
                              setMessageReportFilter("");
                              setMessageReportOpen(true);
                            }}
                          >
                            <i className="ri-message-2-line" />
                            Message Report
                          </button>
                        )}

                        <button
                          type="button"
                          className="attendance-header-btn print"
                          disabled={!filters.batch_id}
                          onClick={printAttendance}
                        >
                          <i className="ri-printer-line" />
                          Print
                        </button>

                        <button
                          type="button"
                          className="attendance-header-btn refresh"
                          disabled={!filters.batch_id}
                          onClick={getStudentAttendanceList}
                        >
                          <i className="ri-refresh-line" />
                          Refresh
                        </button>
                      </div>
                    </div>

                    <div className="attendance-filter-grid">
                      {/* SESSION */}

                      <div className="attendance-field">
                        <label>Session</label>

                        <div className="attendance-input">
                          <i className="ri-calendar-event-line" />

                          <select
                            value={filters.session_id}
                            onChange={(event) => {
                              setData({});
                              setSearch("");
                              setStatusFilter("");

                              setFilters((previous) => ({
                                ...previous,

                                session_id: event.target.value,

                                course_id: "",
                                batch_id: "",
                              }));
                            }}
                          >
                            <option value="">Select Session</option>

                            {sessions.map((session) => (
                              <option key={session.id} value={session.id}>
                                {session.session}

                                {Number(session.status) === 1
                                  ? " - Active"
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* COURSE */}

                      <div className="attendance-field">
                        <label>
                          Course
                          <span>Optional</span>
                        </label>

                        <div className="attendance-input">
                          <i className="ri-book-open-line" />

                          <select
                            value={filters.course_id}
                            onChange={(event) => {
                              setData({});
                              setSearch("");
                              setStatusFilter("");

                              setFilters((previous) => ({
                                ...previous,

                                course_id: event.target.value,

                                batch_id: "",
                              }));
                            }}
                          >
                            <option value="">All Courses</option>

                            {courses.map((course) => (
                              <option key={course.id} value={course.id}>
                                {course.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* BATCH */}

                      <div className="attendance-field attendance-batch-field">
                        <label>
                          Batch
                          <b>*</b>
                        </label>

                        <div className="attendance-input">
                          <i className="ri-group-2-line" />

                          <select
                            value={filters.batch_id}
                            onChange={(event) => {
                              setData({});
                              setSearch("");
                              setStatusFilter("");

                              setFilters((previous) => ({
                                ...previous,

                                batch_id: event.target.value,
                              }));
                            }}
                          >
                            <option value="">Select Batch</option>

                            {batches.map((batch) => (
                              <option key={batch.id} value={batch.id}>
                                {batch.title}

                                {batch?.course?.title
                                  ? ` - ${batch.course.title}`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        {!filters.batch_id && (
                          <small className="batch-required-text">
                            Select batch to load students
                          </small>
                        )}
                      </div>

                      {/* TEACHER */}

                      <div className="attendance-field">
                        <label>
                          Class Teacher
                          <span>Optional</span>
                        </label>

                        <div className="attendance-input">
                          <i className="ri-user-star-line" />

                          <select
                            value={filters.staff_id}
                            onChange={(event) => {
                              setData({});
                              setSearch("");
                              setStatusFilter("");

                              setFilters((previous) => ({
                                ...previous,

                                staff_id: event.target.value,
                              }));
                            }}
                          >
                            <option value="">All Teachers</option>

                            {staffs.map((staff) => (
                              <option key={staff.id} value={staff.id}>
                                {staff.name}

                                {staff.registration_no
                                  ? ` (${staff.registration_no})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* DATE */}

                      <div className="attendance-field">
                        <label>Attendance Date</label>

                        <div className="attendance-input">
                          <i className="ri-calendar-line" />

                          <input
                            type="date"
                            value={filters.date}
                            onChange={(event) => {
                              setData({});
                              setSearch("");
                              setStatusFilter("");

                              setFilters((previous) => ({
                                ...previous,

                                date: event.target.value,
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {filterLoading && (
                      <div className="attendance-filter-loading">
                        <i className="ri-loader-4-line attendance-spin" />
                        Loading filter options...
                      </div>
                    )}
                  </section>

                  {/* =============================================
              NO BATCH
          ============================================= */}

                  {!filters.batch_id && (
                    <section className="attendance-select-batch">
                      <div className="attendance-empty-icon">
                        <i className="ri-group-2-line" />
                      </div>

                      <h2>Select a Batch</h2>

                      <p>
                        Students are not loaded automatically. Select the
                        required batch above to start marking attendance.
                      </p>

                      <div className="attendance-empty-info">
                        {selectedSession && (
                          <span>
                            <i className="ri-calendar-event-line" />
                            {selectedSession.session}
                          </span>
                        )}

                        <span>
                          <i className="ri-calendar-line" />
                          {filters.date}
                        </span>
                      </div>
                    </section>
                  )}

                  {/* =============================================
              ATTENDANCE AREA
          ============================================= */}

                  {filters.batch_id && (
                    <>
                      {/* =========================================
                  MODE
              ========================================= */}

                      <section className="attendance-mode-bar">
                        <div>
                          <span className="attendance-mode-label">
                            Attendance Mode
                          </span>

                          <div className="attendance-mode-buttons">
                            <button
                              type="button"
                              className={
                                attendanceType === "present_absent"
                                  ? "active"
                                  : ""
                              }
                              onClick={() =>
                                changeAttendanceType("present_absent")
                              }
                            >
                              <i className="ri-checkbox-multiple-line" />
                              Present / Absent
                            </button>

                            <button
                              type="button"
                              className={
                                attendanceType === "in_out" ? "active" : ""
                              }
                              onClick={() => changeAttendanceType("in_out")}
                            >
                              <i className="ri-fingerprint-line" />
                              IN / OUT
                            </button>
                          </div>
                        </div>

                        <div className="attendance-current-batch">
                          <span>Batch</span>

                          <strong>{selectedBatch?.title || "-"}</strong>

                          <small>{filters.date}</small>
                        </div>
                      </section>

                      {/* =========================================
                  SUMMARY
              ========================================= */}

                      <section className="attendance-summary">
                        <button
                          type="button"
                          className={`attendance-summary-card total ${statusFilter === "" ? "active" : ""
                            }`}
                          onClick={() => setStatusFilter("")}
                        >
                          <span className="summary-card-icon">
                            <i className="ri-group-line" />
                          </span>

                          <div>
                            <small>Total Students</small>

                            <strong>{totalCount}</strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`attendance-summary-card present ${statusFilter === "1" ? "active" : ""
                            }`}
                          onClick={() =>
                            setStatusFilter(statusFilter === "1" ? "" : "1")
                          }
                        >
                          <span className="summary-card-icon">
                            <i className="ri-checkbox-circle-line" />
                          </span>

                          <div>
                            <small>Present</small>

                            <strong>{countPresent}</strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`attendance-summary-card absent ${statusFilter === "2" ? "active" : ""
                            }`}
                          onClick={() =>
                            setStatusFilter(statusFilter === "2" ? "" : "2")
                          }
                        >
                          <span className="summary-card-icon">
                            <i className="ri-close-circle-line" />
                          </span>

                          <div>
                            <small>Absent</small>

                            <strong>{countAbsent}</strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`attendance-summary-card pending ${statusFilter === "0" ? "active" : ""
                            }`}
                          onClick={() =>
                            setStatusFilter(statusFilter === "0" ? "" : "0")
                          }
                        >
                          <span className="summary-card-icon">
                            <i className="ri-time-line" />
                          </span>

                          <div>
                            <small>Not Marked</small>

                            <strong>{countNotMarked}</strong>
                          </div>
                        </button>
                      </section>

                      {/* =========================================
                  MAIN STUDENT LIST
              ========================================= */}

                      <section className="attendance-student-card">
                        <div className="attendance-list-header">
                          <div>
                            <h2>Student List</h2>

                            <p>
                              {filteredRows.length} student
                              {filteredRows.length === 1 ? "" : "s"}
                              {" • "}
                              {selectedBatch?.title || ""}
                            </p>
                          </div>

                          <div className="attendance-list-actions">
                            {countNotMarked > 0 && (
                              <button
                                type="button"
                                className="mark-all-absent-btn"
                                disabled={saving}
                                onClick={handleMarkAllAbsent}
                              >
                                <i className="ri-user-forbid-line" />
                                Mark All Unmarked Absent
                                <span>{countNotMarked}</span>
                              </button>
                            )}

                            <div className="attendance-search">
                              <i className="ri-search-line" />

                              <input
                                type="text"
                                value={search}
                                placeholder="Search student..."
                                onChange={(event) =>
                                  setSearch(event.target.value)
                                }
                              />

                              {search && (
                                <button
                                  type="button"
                                  onClick={() => setSearch("")}
                                >
                                  <i className="ri-close-line" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* =======================================
                    MOBILE STUDENT CARDS
                ======================================= */}

                        <div className="attendance-mobile-list">
                          {filteredRows.map((item, index) => {
                            const attendance = item?.today_attendance || {};

                            const status = getAttendanceStatus(item);

                            const message = getStudentMessageData(item);

                            const hasIn = !!attendance?.check_in_time;

                            const hasOut = !!attendance?.check_out_time;

                            return (
                              <article
                                className="attendance-mobile-student"
                                key={item?.id || index}
                              >
                                <div className="mobile-student-top">
                                  <div className="attendance-student-info">
                                    <div className="attendance-avatar">
                                      {getInitials(item?.name)}
                                    </div>

                                    <div>
                                      <strong>{item?.name || "-"}</strong>

                                      <span>
                                        {item?.registration_no || "-"}

                                        {item?.phone ? ` • ${item.phone}` : ""}
                                      </span>
                                    </div>
                                  </div>

                                  <span
                                    className={`attendance-status ${status.className}`}
                                  >
                                    <i className={status.icon} />

                                    {status.label}
                                  </span>
                                </div>

                                {(hasIn || hasOut) && (
                                  <div className="mobile-time-row">
                                    <span>
                                      IN:
                                      <strong>
                                        {safeTime(attendance?.check_in_time)}
                                      </strong>
                                    </span>

                                    <span>
                                      OUT:
                                      <strong>
                                        {safeTime(attendance?.check_out_time)}
                                      </strong>
                                    </span>
                                  </div>
                                )}

                                <div className="mobile-attendance-actions">
                                  {attendanceType === "present_absent" ? (
                                    <>
                                      <button
                                        type="button"
                                        className="student-action present"
                                        disabled={saving}
                                        title="Mark Present"
                                        onClick={() =>
                                          updateAttendance(
                                            item?.registration_no,
                                            "present",
                                          )
                                        }
                                      >
                                        <i className="ri-check-line" />
                                        Present
                                      </button>

                                      <button
                                        type="button"
                                        className="student-action absent"
                                        disabled={saving}
                                        title="Mark Absent"
                                        onClick={() =>
                                          updateAttendance(
                                            item?.registration_no,
                                            "absent",
                                          )
                                        }
                                      >
                                        <i className="ri-close-line" />
                                        Absent
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      {!hasIn && status.key === "not_marked" && (
                                        <button
                                          type="button"
                                          className="student-action punch"
                                          disabled={saving}
                                          onClick={() => openPunchModal(item)}
                                        >
                                          <i className="ri-login-box-line" />
                                          Punch IN
                                        </button>
                                      )}

                                      {hasIn && !hasOut && (
                                        <button
                                          type="button"
                                          className="student-action punch-out"
                                          disabled={saving}
                                          onClick={() => openPunchModal(item)}
                                        >
                                          <i className="ri-logout-box-r-line" />
                                          Punch OUT
                                        </button>
                                      )}

                                      {/* {(status.key === "present" || hasIn || hasOut) && (
                                        <button
                                          type="button"
                                          className="student-action absent"
                                          disabled={saving}
                                          title="Mark Absent"
                                          onClick={() =>
                                            updateAttendance(
                                              item?.registration_no,
                                              "absent",
                                            )
                                          }
                                        >
                                          <i className="ri-close-line" />
                                          Mark Absent
                                        </button>
                                      )} */}

                                      {hasIn && hasOut && (
                                        <span className="attendance-complete">
                                          <i className="ri-check-double-line" />
                                          Completed
                                        </span>
                                      )}
                                    </>
                                  )}

                                  {status.key !== "not_marked" && (
                                    <button
                                      type="button"
                                      className={`student-action delivery ${message.hasDelivered
                                        ? "delivered"
                                        : "not-delivered"
                                        }`}
                                      onClick={() => openMessageStudent(item)}
                                    >
                                      <i
                                        className={
                                          message.hasDelivered
                                            ? "ri-checkbox-circle-line"
                                            : message.hasProcessing
                                              ? "ri-loader-4-line"
                                              : "ri-message-2-line"
                                        }
                                      />

                                      <span>{message.deliveryLabel}</span>

                                      {message.hasDelivered &&
                                        message.deliveredChannels.length >
                                        0 && (
                                          <small className="mobile-delivery-channels">
                                            {message.deliveredChannels
                                              .map((channel) => channel.label)
                                              .join(", ")}
                                          </small>
                                        )}
                                    </button>
                                  )}
                                </div>
                              </article>
                            );
                          })}
                        </div>

                        {/* =======================================
                    DESKTOP TABLE
                ======================================= */}

                        <div className="attendance-table-scroll">
                          <table className="attendance-table">
                            <thead>
                              <tr>
                                <th className="serial">#</th>

                                <th className="student-col">Student</th>

                                <th>Batch</th>

                                <th>Attendance</th>

                                <th>Time</th>

                                <th>Message</th>

                                <th>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {filteredRows.map((item, index) => {
                                const attendance = item?.today_attendance || {};

                                const status = getAttendanceStatus(item);

                                const message = getStudentMessageData(item);

                                const hasIn = !!attendance?.check_in_time;

                                const hasOut = !!attendance?.check_out_time;

                                const batch =
                                  item?.latest_course_and_fee?.batch?.title ||
                                  selectedBatch?.title ||
                                  "-";

                                return (
                                  <tr key={item?.id || index}>
                                    <td className="serial">{index + 1}</td>

                                    {/* STUDENT */}

                                    <td className="student-col">
                                      <div className="attendance-student-info">
                                        <div className="attendance-avatar">
                                          {getInitials(item?.name)}
                                        </div>

                                        <div>
                                          <strong>{item?.name || "-"}</strong>

                                          <span>
                                            {item?.registration_no || "-"}
                                          </span>

                                          {item?.phone && (
                                            <small>
                                              <i className="ri-phone-line" />
                                              {item.phone}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* BATCH */}

                                    <td>
                                      <span className="attendance-batch-badge">
                                        {batch}
                                      </span>
                                    </td>

                                    {/* STATUS */}

                                    <td>
                                      <span
                                        className={`attendance-status ${status.className}`}
                                      >
                                        <i className={status.icon} />

                                        {status.label}
                                      </span>
                                    </td>

                                    {/* TIME */}

                                    <td>
                                      {hasIn || hasOut ? (
                                        <div className="attendance-times">
                                          <span>
                                            <b>IN</b>
                                            {safeTime(
                                              attendance?.check_in_time,
                                            )}
                                          </span>

                                          <span>
                                            <b>OUT</b>
                                            {safeTime(
                                              attendance?.check_out_time,
                                            )}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className="attendance-dash">
                                          -
                                        </span>
                                      )}
                                    </td>

                                    {/* MESSAGE */}

                                    <td>
                                      {status.key === "not_marked" ? (
                                        <span className="attendance-dash">
                                          -
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          className={`message-delivery-btn ${message.hasDelivered
                                            ? "has-delivery"
                                            : message.hasProcessing
                                              ? "processing-delivery"
                                              : "needs-attention"
                                            }`}
                                          onClick={() =>
                                            openMessageStudent(item)
                                          }
                                        >
                                          <i
                                            className={
                                              message.hasDelivered
                                                ? "ri-checkbox-circle-line"
                                                : message.hasProcessing
                                                  ? "ri-loader-4-line"
                                                  : "ri-message-2-line"
                                            }
                                          />

                                          <span className="delivery-main-text">
                                            {message.deliveryLabel}
                                          </span>

                                          {message.hasDelivered &&
                                            message.deliveredChannels.length >
                                            0 && (
                                              <span className="delivery-channel-text">
                                                {message.deliveredChannels
                                                  .map(
                                                    (channel) => channel.label,
                                                  )
                                                  .join(" + ")}
                                              </span>
                                            )}

                                          {!message.hasDelivered &&
                                            message.hasFailed && (
                                              <span className="delivery-dot" />
                                            )}
                                        </button>
                                      )}
                                    </td>

                                    {/* ACTION */}

                                    <td>
                                      <div className="student-table-actions">
                                        {attendanceType === "present_absent" ? (
                                          <>
                                            <button
                                              type="button"
                                              className="table-action present"
                                              disabled={saving}
                                              title="Mark Present"
                                              onClick={() =>
                                                updateAttendance(
                                                  item?.registration_no,
                                                  "present",
                                                )
                                              }
                                            >
                                              <i className="ri-check-line" />
                                              Present
                                            </button>

                                            <button
                                              type="button"
                                              className="table-action absent"
                                              disabled={saving}
                                              title="Mark Absent"
                                              onClick={() =>
                                                updateAttendance(
                                                  item?.registration_no,
                                                  "absent",
                                                )
                                              }
                                            >
                                              <i className="ri-close-line" />
                                              Absent
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            {!hasIn && status.key === "not_marked" && (
                                              <button
                                                type="button"
                                                className="table-action punch"
                                                disabled={saving}
                                                onClick={() => openPunchModal(item)}
                                              >
                                                <i className="ri-login-box-line" />
                                                Punch IN
                                              </button>
                                            )}

                                            {hasIn && !hasOut && (
                                              <button
                                                type="button"
                                                className="table-action punch-out"
                                                disabled={saving}
                                                onClick={() => openPunchModal(item)}
                                              >
                                                <i className="ri-logout-box-r-line" />
                                                Punch OUT
                                              </button>
                                            )}

                                            {/* {(status.key === "present" || hasIn || hasOut) && (
                                              <button
                                                type="button"
                                                className="table-action absent"
                                                disabled={saving}
                                                title="Mark Absent"
                                                onClick={() =>
                                                  updateAttendance(
                                                    item?.registration_no,
                                                    "absent",
                                                  )
                                                }
                                              >
                                                <i className="ri-close-line" />
                                                Mark Absent
                                              </button>
                                            )} */}

                                            {hasIn && hasOut && (
                                              <span className="attendance-complete">
                                                <i className="ri-check-double-line" />
                                                Completed
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}

                              {!loading && filteredRows.length === 0 && (
                                <tr>
                                  <td
                                    colSpan="7"
                                    className="attendance-no-data"
                                  >
                                    <i className="ri-user-search-line" />

                                    <strong>No Students Found</strong>

                                    <span>
                                      No students match the selected filters.
                                    </span>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="attendance-list-footer">
                          <span>
                            <i className="ri-information-line" />
                            Click Present / Absent to update individual
                            students.
                          </span>

                          <strong>{filteredRows.length} students</strong>
                        </div>
                      </section>
                    </>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================
          PUNCH MODAL
      ================================================= */}

      {punchStudent && (
        <div className="attendance-modal-backdrop">
          <div className="attendance-modal punch-modal">
            <div className="attendance-modal-header">
              <div>
                <h3>
                  <i className="ri-fingerprint-line" />

                  {punchStudent?.today_attendance?.check_in_time
                    ? "Punch OUT"
                    : "Punch IN"}
                </h3>

                <p>Manual attendance time</p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setPunchStudent(null);
                  setPunchTime("");
                }}
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="attendance-modal-body">
              <div className="modal-student">
                <div className="attendance-avatar large">
                  {getInitials(punchStudent?.name)}
                </div>

                <div>
                  <strong>{punchStudent?.name}</strong>

                  <span>{punchStudent?.registration_no}</span>
                </div>
              </div>

              <div className="punch-date-info">
                <span>
                  <i className="ri-calendar-line" />
                  {filters.date}
                </span>

                <span>
                  <i className="ri-group-line" />
                  {selectedBatch?.title}
                </span>
              </div>

              <div className="attendance-field">
                <label>Punch Time</label>

                <div className="attendance-input">
                  <i className="ri-time-line" />

                  <input
                    type="time"
                    value={punchTime}
                    onChange={(event) => setPunchTime(event.target.value)}
                  />
                </div>
              </div>

              <div className="message-info-box">
                <i className="ri-message-2-line" />

                <div>
                  <strong>Attendance Message</strong>

                  <span>
                    Enabled attendance messages will be triggered with this
                    punch.
                  </span>
                </div>
              </div>
            </div>

            <div className="attendance-modal-footer">
              <button
                type="button"
                className="modal-cancel-btn"
                disabled={saving}
                onClick={() => {
                  setPunchStudent(null);
                  setPunchTime("");
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-primary-btn"
                disabled={saving || !punchTime}
                onClick={handlePunch}
              >
                <i className="ri-fingerprint-line" />

                {punchStudent?.today_attendance?.check_in_time
                  ? "Confirm Punch OUT"
                  : "Confirm Punch IN"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================
          INDIVIDUAL MESSAGE DELIVERY / HISTORY
      ================================================= */}
      {messageStudent &&
        (() => {
          const message = getStudentMessageData(messageStudent);

          const attendance = messageStudent?.today_attendance || {};

          const retryableEvents = message.history.filter(
            (event) => !event.hasDelivered || event.hasFailed,
          );

          const showRetry = retryableEvents.length > 0;

          const getOverallClass = () => {
            if (message.hasDelivered) {
              return "delivered";
            }

            if (message.hasProcessing) {
              return "processing";
            }

            if (message.hasFailed) {
              return "failed";
            }

            return "not-delivered";
          };

          return (
            <div className="attendance-modal-backdrop">
              <div className="attendance-modal message-modal message-history-modal">
                <div className="attendance-modal-header">
                  <div>
                    <h3>
                      <i className="ri-message-2-line" />
                      Message Delivery
                    </h3>

                    <p>Student attendance message history</p>
                  </div>

                  <button type="button" onClick={() => setMessageStudent(null)}>
                    <i className="ri-close-line" />
                  </button>
                </div>

                <div className="attendance-modal-body">
                  <div className="modal-student">
                    <div className="attendance-avatar large">
                      {getInitials(messageStudent?.name)}
                    </div>

                    <div>
                      <strong>{messageStudent?.name}</strong>

                      <span>
                        {messageStudent?.registration_no}

                        {messageStudent?.phone
                          ? ` • ${messageStudent.phone}`
                          : ""}
                      </span>
                    </div>
                  </div>

                  {/* OVERALL DELIVERY */}
                  <div className={`overall-delivery-box ${getOverallClass()}`}>
                    <div className="overall-delivery-icon">
                      <i
                        className={
                          message.hasDelivered
                            ? "ri-checkbox-circle-line"
                            : message.hasProcessing
                              ? "ri-loader-4-line"
                              : message.hasFailed
                                ? "ri-error-warning-line"
                                : "ri-close-circle-line"
                        }
                      />
                    </div>

                    <div>
                      <small>Overall Message Status</small>

                      <strong>{message.deliveryLabel}</strong>

                      {message.hasDelivered ? (
                        <span>
                          Delivered via{" "}
                          {message.deliveredChannels
                            .map((channel) => channel.label)
                            .join(" + ")}
                        </span>
                      ) : (
                        <span>
                          No successful WhatsApp/Meta or Push delivery found.
                        </span>
                      )}
                    </div>
                  </div>

                  {/* DELIVERY HISTORY */}
                  <div className="message-history-heading">
                    <div>
                      <strong>Message History</strong>

                      <span>Check IN and Check OUT delivery details</span>
                    </div>

                    <span className="history-count">
                      {message.history.length}
                    </span>
                  </div>

                  <div className="message-history-list">
                    {message.history.map((event) => (
                      <div className="message-history-event" key={event.key}>
                        <div className="history-event-header">
                          <div>
                            <span className={`history-event-icon ${event.key}`}>
                              <i
                                className={
                                  event.key === "in"
                                    ? "ri-login-box-line"
                                    : event.key === "out"
                                      ? "ri-logout-box-r-line"
                                      : "ri-calendar-check-line"
                                }
                              />
                            </span>

                            <div>
                              <strong>{event.label}</strong>

                              <small>
                                {event.time
                                  ? safeTime(event.time)
                                  : filters.date}
                              </small>
                            </div>
                          </div>

                          <span
                            className={`history-overall-status ${event.hasDelivered
                              ? "delivered"
                              : event.hasProcessing
                                ? "processing"
                                : event.hasFailed
                                  ? "failed"
                                  : "not-delivered"
                              }`}
                          >
                            <i
                              className={
                                event.hasDelivered
                                  ? "ri-check-line"
                                  : event.hasProcessing
                                    ? "ri-loader-4-line"
                                    : event.hasFailed
                                      ? "ri-error-warning-line"
                                      : "ri-close-line"
                              }
                            />

                            {event.deliveryLabel}
                          </span>
                        </div>

                        <div className="history-channel-grid">
                          <div
                            className={`history-channel ${event.meta.delivered
                              ? "delivered"
                              : event.meta.processing
                                ? "processing"
                                : event.meta.failed
                                  ? "failed"
                                  : "not-delivered"
                              }`}
                          >
                            <span className="history-channel-icon whatsapp">
                              <i className="ri-whatsapp-line" />
                            </span>

                            <div>
                              <small>WhatsApp / Meta</small>

                              <strong>{event.meta.status.label}</strong>
                            </div>

                            <i
                              className={
                                event.meta.delivered
                                  ? "ri-checkbox-circle-fill"
                                  : event.meta.processing
                                    ? "ri-loader-4-line"
                                    : event.meta.failed
                                      ? "ri-error-warning-fill"
                                      : "ri-close-circle-line"
                              }
                            />
                          </div>

                          <div
                            className={`history-channel ${event.push.delivered
                              ? "delivered"
                              : event.push.processing
                                ? "processing"
                                : event.push.failed
                                  ? "failed"
                                  : "not-delivered"
                              }`}
                          >
                            <span className="history-channel-icon push">
                              <i className="ri-notification-3-line" />
                            </span>

                            <div>
                              <small>Push Notification</small>

                              <strong>{event.push.status.label}</strong>
                            </div>

                            <i
                              className={
                                event.push.delivered
                                  ? "ri-checkbox-circle-fill"
                                  : event.push.processing
                                    ? "ri-loader-4-line"
                                    : event.push.failed
                                      ? "ri-error-warning-fill"
                                      : "ri-close-circle-line"
                              }
                            />
                          </div>
                        </div>

                        {event.hasDelivered && (
                          <div className="history-delivered-via">
                            <i className="ri-check-double-line" />

                            <span>
                              {event.label} message delivered via{" "}
                              <strong>
                                {event.deliveredChannels
                                  .map((channel) => channel.label)
                                  .join(" + ")}
                              </strong>
                            </span>
                          </div>
                        )}
                      </div>
                    ))}

                    {message.history.length === 0 && (
                      <div className="message-history-empty">
                        <i className="ri-inbox-line" />

                        <strong>No Message History</strong>

                        <span>
                          No attendance message status is available for this
                          student.
                        </span>
                      </div>
                    )}
                  </div>

                  {/* RAW GENERAL STATUS - useful for manual/present-absent attendance */}
                  {!attendance?.check_in_time &&
                    !attendance?.check_out_time && (
                      <div className="general-message-status">
                        <div>
                          <span className="delivery-channel-icon meta">
                            <i className="ri-whatsapp-line" />
                          </span>

                          <div>
                            <strong>WhatsApp / Meta</strong>
                            <small>General attendance message</small>
                          </div>
                        </div>

                        <span
                          className={`delivery-status ${message.generalEvent.meta.status.className}`}
                        >
                          <i
                            className={message.generalEvent.meta.status.icon}
                          />
                          {message.generalEvent.meta.status.label}
                        </span>

                        <div>
                          <span className="delivery-channel-icon push">
                            <i className="ri-notification-3-line" />
                          </span>

                          <div>
                            <strong>Push Notification</strong>
                            <small>General app notification</small>
                          </div>
                        </div>

                        <span
                          className={`delivery-status ${message.generalEvent.push.status.className}`}
                        >
                          <i
                            className={message.generalEvent.push.status.icon}
                          />
                          {message.generalEvent.push.status.label}
                        </span>
                      </div>
                    )}

                  {showRetry && (
                    <div className="message-warning-box">
                      <i className="ri-information-line" />

                      <div>
                        <strong>Delivery needs attention</strong>

                        <span>
                          One or more WhatsApp/Meta or Push attendance messages
                          are not delivered yet.
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="attendance-modal-footer">
                  <button
                    type="button"
                    className="modal-cancel-btn"
                    onClick={() => setMessageStudent(null)}
                  >
                    Close
                  </button>

                  {showRetry && (
                    <button
                      type="button"
                      className="message-retry-btn"
                      onClick={resendAttendanceMessage}
                    >
                      <i className="ri-send-plane-line" />
                      Send Message
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {/* =================================================
          MESSAGE REPORT
      ================================================= */}

      {messageReportOpen && (
        <div className="attendance-modal-backdrop">
          <div className="attendance-modal message-report-modal">
            <div className="attendance-modal-header">
              <div>
                <h3>
                  <i className="ri-bar-chart-box-line" />
                  Message Report
                </h3>

                <p>
                  {selectedBatch?.title}
                  {" • "}
                  {filters.date}
                </p>
              </div>

              <button type="button" onClick={() => setMessageReportOpen(false)}>
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="attendance-modal-body">
              {/* SUMMARY */}

              <div className="message-report-grid">
                <div className="message-report-channel">
                  <div className="message-channel-title">
                    <span className="delivery-channel-icon meta">
                      <i className="ri-whatsapp-line" />
                    </span>

                    <div>
                      <strong>WhatsApp / Message</strong>

                      <small>Delivery status</small>
                    </div>
                  </div>

                  <div className="message-count-grid">
                    <button
                      type="button"
                      className="message-count sent"
                      onClick={() => setMessageReportFilter("meta_sent")}
                    >
                      <small>Delivered</small>

                      <strong>{messageSummary.meta.sent}</strong>
                    </button>

                    <button
                      type="button"
                      className="message-count pending"
                      onClick={() => setMessageReportFilter("meta_not_sent")}
                    >
                      <small>Not Delivered</small>

                      <strong>
                        {messageSummary.meta.not_sent +
                          messageSummary.meta.failed}
                      </strong>
                    </button>

                    <div className="message-count processing">
                      <small>Processing</small>

                      <strong>{messageSummary.meta.processing}</strong>
                    </div>
                  </div>
                </div>

                <div className="message-report-channel">
                  <div className="message-channel-title">
                    <span className="delivery-channel-icon push">
                      <i className="ri-notification-3-line" />
                    </span>

                    <div>
                      <strong>Push Notification</strong>

                      <small>App delivery status</small>
                    </div>
                  </div>

                  <div className="message-count-grid">
                    <button
                      type="button"
                      className="message-count sent"
                      onClick={() => setMessageReportFilter("push_sent")}
                    >
                      <small>Delivered</small>

                      <strong>{messageSummary.push.sent}</strong>
                    </button>

                    <button
                      type="button"
                      className="message-count pending"
                      onClick={() => setMessageReportFilter("push_not_sent")}
                    >
                      <small>Not Delivered</small>

                      <strong>
                        {messageSummary.push.not_sent +
                          messageSummary.push.failed}
                      </strong>
                    </button>

                    <div className="message-count processing">
                      <small>Processing</small>

                      <strong>{messageSummary.push.processing}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* FILTER */}

              <div className="message-report-list-header">
                <div>
                  <strong>Student Delivery</strong>

                  <span>{messageReportRows.length} students</span>
                </div>

                {messageReportFilter && (
                  <button
                    type="button"
                    onClick={() => setMessageReportFilter("")}
                  >
                    <i className="ri-close-line" />
                    Clear Filter
                  </button>
                )}
              </div>

              {/* LIST */}

              <div className="message-student-list">
                {messageReportRows.map((item, index) => {
                  const message = getStudentMessageData(item);

                  return (
                    <button
                      type="button"
                      className="message-student-row"
                      key={item?.id || index}
                      onClick={() => {
                        setMessageReportOpen(false);

                        setMessageStudent(item);
                      }}
                    >
                      <div className="attendance-student-info">
                        <div className="attendance-avatar">
                          {getInitials(item?.name)}
                        </div>

                        <div>
                          <strong>{item?.name || "-"}</strong>

                          <span>{item?.registration_no || "-"}</span>
                        </div>
                      </div>

                      <div className="message-row-statuses">
                        <span
                          className={`delivery-status ${message.hasDelivered
                            ? "message-sent"
                            : message.hasProcessing
                              ? "message-processing"
                              : "message-not-sent"
                            }`}
                        >
                          <i
                            className={
                              message.hasDelivered
                                ? "ri-checkbox-circle-line"
                                : message.hasProcessing
                                  ? "ri-loader-4-line"
                                  : "ri-close-circle-line"
                            }
                          />

                          {message.deliveryLabel}
                        </span>

                        {message.hasDelivered &&
                          message.deliveredChannels.map((channel) => (
                            <span
                              className="delivery-status message-sent delivery-channel-chip"
                              key={channel.key}
                            >
                              <i className={channel.icon} />
                              {channel.label}
                            </span>
                          ))}

                        <i className="ri-arrow-right-s-line row-arrow" />
                      </div>
                    </button>
                  );
                })}

                {messageReportRows.length === 0 && (
                  <div className="message-report-empty">
                    <i className="ri-inbox-line" />

                    <strong>No students found</strong>

                    <span>No message records match this filter.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================================================
          CSS
      ================================================= */}

      <style>{`

        .attendance-manual-page {
          min-height: 100vh;
          background: #f6f7fb;
        }

        .attendance-manual-page .layout-container {
          min-height: 100vh;
        }

        .attendance-manual-page .layout-page {
          min-width: 0;
          min-height: 100vh;
        }

        .attendance-manual-content-wrapper {
          width: 100%;
          min-width: 0;
          background: #f6f7fb;
        }

        .attendance-manual-container {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          padding: 16px 18px 40px;
        }

        .attendance-master-wrapper {
          width: 100%;
          min-width: 0;
          margin-bottom: 18px;
        }

        .attendance-manual-content {
          width: 100%;
          max-width: 1600px;
          min-width: 0;
          margin: 0 auto;
          padding: 0;
        }

        /* ==============================================
           HEADER
        ============================================== */

        .attendance-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;

          padding: 20px;

          margin-bottom: 15px;

          background: #fff;

          border: 1px solid #e2e5eb;
          border-radius: 14px;

          box-shadow:
            0 4px 18px
            rgba(30,35,55,.04);
        }

        .attendance-page-title {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        .attendance-title-icon {
          width: 52px;
          height: 52px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 52px;

          border-radius: 12px;

          background: #eef0ff;
          color: #696cff;

          font-size: 25px;
        }

        .attendance-breadcrumb {
          display: flex;
          align-items: center;
          gap: 3px;

          margin-bottom: 4px;

          color: #9398a3;

          font-size: 12px;
          font-weight: 600;
        }

        .attendance-page-header h1 {
          margin: 0;

          color: #3d424c;

          font-size: 25px;
          font-weight: 700;
        }

        .attendance-page-header p {
          margin: 5px 0 0;

          color: #8d929d;

          font-size: 13px;
        }

        .attendance-header-actions {
          display: flex;
          align-items: center;
          gap: 7px;

          flex-shrink: 0;
        }

        .attendance-header-btn {
          min-height: 42px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 6px;

          padding: 8px 13px;

          border-radius: 8px;

          font-size: 12px;
          font-weight: 700;

          cursor: pointer;
        }

        .attendance-header-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .attendance-header-btn.message {
          border: 1px solid #d9dafc;

          background: #f5f5ff;
          color: #696cff;
        }

        .attendance-header-btn.print {
          border: 1px solid #dfe2e8;

          background: #fff;
          color: #5d626c;
        }

        .attendance-header-btn.refresh {
          border: 1px solid #696cff;

          background: #696cff;
          color: #fff;
        }

        /* ==============================================
           FILTER
        ============================================== */

        .attendance-filter-card {
          padding: 18px;

          margin-bottom: 14px;

          border: 1px solid #e2e5eb;
          border-radius: 13px;

          background: #fff;
        }

        .attendance-filter-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          margin-bottom: 16px;
        }

        .attendance-filter-header h2 {
          display: flex;
          align-items: center;
          gap: 6px;

          margin: 0;

          color: #414650;

          font-size: 17px;
          font-weight: 700;
        }

        .attendance-filter-header h2 i {
          color: #696cff;
        }

        .attendance-filter-header p {
          margin: 4px 0 0;

          color: #9499a4;

          font-size: 12px;
        }

        .attendance-reset-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          padding: 7px 11px;

          border: 1px solid #e0e3e9;
          border-radius: 7px;

          background: #fff;
          color: #656a75;

          font-size: 12px;
          font-weight: 700;

          cursor: pointer;
        }

        .attendance-filter-grid {
          display: grid;

          grid-template-columns:
            repeat(
              5,
              minmax(0,1fr)
            );

          gap: 12px;
        }

        .attendance-field label {
          display: flex;
          align-items: center;

          gap: 5px;

          margin-bottom: 6px;

          color: #555a64;

          font-size: 13px;
          font-weight: 700;
        }

        .attendance-field label span {
          color: #a0a5af;

          font-size: 9px;
          font-weight: 500;
        }

        .attendance-field label b {
          color: #d44849;
        }

        .attendance-input {
          position: relative;
        }

        .attendance-input > i {
          position: absolute;

          top: 50%;
          left: 11px;

          z-index: 2;

          transform:
            translateY(-50%);

          color: #9499a4;

          font-size: 16px;

          pointer-events: none;
        }

        .attendance-input select,
        .attendance-input input {
          width: 100%;
          height: 45px;

          padding:
            8px 10px 8px 36px;

          border: 1px solid #dce0e7;
          border-radius: 8px;

          outline: none;

          background: #fff;
          color: #4d525c;

          font-size: 13px;
          font-weight: 500;
        }

        .attendance-input select:focus,
        .attendance-input input:focus {
          border-color: #9295ff;

          box-shadow:
            0 0 0 3px
            rgba(105,108,255,.08);
        }

        .attendance-batch-field
        .attendance-input select {
          border-color: #b9bbff;

          background: #fbfbff;
        }

        .batch-required-text {
          display: block;

          margin-top: 5px;

          color: #696cff;

          font-size: 10px;
          font-weight: 600;
        }

        .attendance-filter-loading {
          margin-top: 10px;

          color: #7f848e;

          font-size: 11px;
        }

        /* ==============================================
           EMPTY
        ============================================== */

        .attendance-select-batch {
          min-height: 380px;

          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          padding: 40px 20px;

          border: 1px dashed #cfd3df;
          border-radius: 14px;

          background: #fff;

          text-align: center;
        }

        .attendance-empty-icon {
          width: 72px;
          height: 72px;

          display: flex;
          align-items: center;
          justify-content: center;

          margin-bottom: 15px;

          border-radius: 50%;

          background: #eef0ff;
          color: #696cff;

          font-size: 31px;
        }

        .attendance-select-batch h2 {
          margin: 0;

          color: #414650;

          font-size: 21px;
        }

        .attendance-select-batch p {
          max-width: 500px;

          margin: 8px auto 0;

          color: #8c919c;

          font-size: 13px;

          line-height: 1.6;
        }

        .attendance-empty-info {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;

          gap: 7px;

          margin-top: 17px;
        }

        .attendance-empty-info span {
          display: inline-flex;
          align-items: center;

          gap: 5px;

          padding: 7px 10px;

          border: 1px solid #e2e5eb;
          border-radius: 7px;

          color: #666b75;

          font-size: 11px;
          font-weight: 600;
        }

        /* ==============================================
           MODE
        ============================================== */

        .attendance-mode-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          padding: 12px 14px;

          margin-bottom: 12px;

          border: 1px solid #e1e4ea;
          border-radius: 11px;

          background: #fff;
        }

        .attendance-mode-label {
          display: block;

          margin-bottom: 6px;

          color: #858a95;

          font-size: 10px;
          font-weight: 700;

          text-transform: uppercase;
        }

        .attendance-mode-buttons {
          display: flex;

          padding: 3px;

          border-radius: 8px;

          background: #f1f2f5;
        }

        .attendance-mode-buttons button {
          display: inline-flex;
          align-items: center;
          gap: 5px;

          padding: 8px 11px;

          border: 0;
          border-radius: 6px;

          background: transparent;
          color: #676c76;

          font-size: 12px;
          font-weight: 700;

          cursor: pointer;
        }

        .attendance-mode-buttons button.active {
          background: #fff;
          color: #696cff;

          box-shadow:
            0 2px 8px
            rgba(30,35,55,.09);
        }

        .attendance-current-batch {
          display: flex;
          align-items: center;

          gap: 7px;
        }

        .attendance-current-batch span,
        .attendance-current-batch small {
          color: #9095a0;

          font-size: 10px;
        }

        .attendance-current-batch strong {
          padding: 6px 9px;

          border-radius: 6px;

          background: #eef0ff;
          color: #696cff;

          font-size: 12px;
        }

        /* ==============================================
           SUMMARY
        ============================================== */

        .attendance-summary {
          display: grid;

          grid-template-columns:
            repeat(4,1fr);

          gap: 10px;

          margin-bottom: 12px;
        }

        .attendance-summary-card {
          display: flex;
          align-items: center;

          gap: 11px;

          padding: 13px;

          border: 1px solid #e2e5eb;
          border-radius: 10px;

          background: #fff;
          color: inherit;

          text-align: left;

          cursor: pointer;
        }

        .attendance-summary-card.active {
          border-color: #696cff;

          box-shadow:
            0 0 0 2px
            rgba(105,108,255,.07);
        }

        .summary-card-icon {
          width: 39px;
          height: 39px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 39px;

          border-radius: 9px;

          font-size: 18px;
        }

        .attendance-summary-card.total
        .summary-card-icon {
          background: #eef0ff;
          color: #696cff;
        }

        .attendance-summary-card.present
        .summary-card-icon {
          background: #e8f8ef;
          color: #258b52;
        }

        .attendance-summary-card.absent
        .summary-card-icon {
          background: #ffeded;
          color: #d34849;
        }

        .attendance-summary-card.pending
        .summary-card-icon {
          background: #fff3e2;
          color: #c47b1c;
        }

        .attendance-summary-card div {
          display: flex;
          flex-direction: column;
        }

        .attendance-summary-card small {
          color: #8c919c;

          font-size: 10px;
          font-weight: 600;
        }

        .attendance-summary-card strong {
          margin-top: 2px;

          color: #3e434d;

          font-size: 22px;

          line-height: 1;
        }

        /* ==============================================
           STUDENT CARD
        ============================================== */

        .attendance-student-card {
          overflow: hidden;

          border: 1px solid #e1e4ea;
          border-radius: 13px;

          background: #fff;
        }

        .attendance-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          padding: 15px;

          border-bottom:
            1px solid #e8eaef;
        }

        .attendance-list-header h2 {
          margin: 0;

          color: #414650;

          font-size: 17px;
        }

        .attendance-list-header p {
          margin: 4px 0 0;

          color: #9297a2;

          font-size: 11px;
        }

        .attendance-list-actions {
          display: flex;
          align-items: center;

          gap: 7px;
        }

        .mark-all-absent-btn {
          min-height: 40px;

          display: inline-flex;
          align-items: center;

          gap: 5px;

          padding: 7px 10px;

          border: 1px solid #f0cccc;
          border-radius: 8px;

          background: #fff4f4;
          color: #c74344;

          font-size: 11px;
          font-weight: 700;

          cursor: pointer;
        }

        .mark-all-absent-btn span {
          min-width: 20px;
          height: 20px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          border-radius: 20px;

          background: #d34849;
          color: #fff;

          font-size: 9px;
        }

        .attendance-search {
          position: relative;
        }

        .attendance-search > i {
          position: absolute;

          top: 50%;
          left: 10px;

          transform:
            translateY(-50%);

          color: #969ba5;

          pointer-events: none;
        }

        .attendance-search input {
          width: 240px;
          height: 40px;

          padding:
            7px 31px 7px 31px;

          border: 1px solid #dfe2e8;
          border-radius: 8px;

          outline: none;

          color: #4e535d;

          font-size: 12px;
        }

        .attendance-search input:focus {
          border-color: #9295ff;
        }

        .attendance-search button {
          position: absolute;

          top: 50%;
          right: 6px;

          transform:
            translateY(-50%);

          border: 0;

          background: transparent;
          color: #999;

          cursor: pointer;
        }

        /* ==============================================
           TABLE
        ============================================== */

        .attendance-table-scroll {
          width: 100%;

          overflow: auto;

          max-height: 68vh;
        }

        .attendance-table {
          width: 100%;

          min-width: 1050px;

          border-collapse:
            separate;

          border-spacing: 0;
        }

        .attendance-table th {
          position: sticky;

          top: 0;

          z-index: 10;

          padding: 11px 9px;

          border-right:
            1px solid #e3e6eb;

          border-bottom:
            1px solid #dde1e7;

          background: #f5f6f8;
          color: #606570;

          font-size: 11px;
          font-weight: 700;

          text-align: center;

          white-space: nowrap;
        }

        .attendance-table td {
          padding: 10px 9px;

          border-right:
            1px solid #eceef2;

          border-bottom:
            1px solid #eceef2;

          color: #535862;

          font-size: 12px;

          text-align: center;
          vertical-align: middle;
        }

        .attendance-table tbody
        tr:hover td {
          background: #fafbfc;
        }

        .attendance-table
        .serial {
          width: 50px;
        }

        .attendance-table
        .student-col {
          min-width: 220px;

          text-align: left;
        }

        .attendance-student-info {
          display: flex;
          align-items: center;

          gap: 9px;
        }

        .attendance-avatar {
          width: 38px;
          height: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 38px;

          border-radius: 50%;

          background: #eef0ff;
          color: #696cff;

          font-size: 11px;
          font-weight: 800;
        }

        .attendance-avatar.large {
          width: 48px;
          height: 48px;

          flex-basis: 48px;

          font-size: 14px;
        }

        .attendance-student-info
        > div:last-child {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .attendance-student-info strong {
          color: #414650;

          font-size: 13px;
        }

        .attendance-student-info span {
          margin-top: 1px;

          color: #8f949f;

          font-size: 10px;
        }

        .attendance-student-info small {
          display: flex;
          align-items: center;

          gap: 3px;

          margin-top: 2px;

          color: #9a9faa;

          font-size: 9px;
        }

        .attendance-batch-badge {
          display: inline-flex;

          padding: 5px 8px;

          border-radius: 6px;

          background: #eaf6ff;
          color: #3686bc;

          font-size: 10px;
          font-weight: 700;
        }

        .attendance-status {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 4px;

          padding: 6px 8px;

          border-radius: 6px;

          font-size: 10px;
          font-weight: 700;

          white-space: nowrap;
        }

        .attendance-present {
          background: #e8f8ef;
          color: #278c54;
        }

        .attendance-absent {
          background: #ffeded;
          color: #d34849;
        }

        .attendance-not-marked {
          background: #fff3e2;
          color: #bf761b;
        }

        .attendance-times {
          display: flex;
          flex-direction: column;

          gap: 3px;
        }

        .attendance-times span {
          display: flex;
          align-items: center;
          justify-content: center;

          gap: 5px;

          color: #686d77;

          font-size: 10px;
        }

        .attendance-times b {
          color: #999ea8;

          font-size: 8px;
        }

        .attendance-dash {
          color: #aaa;
        }

        /* ==============================================
           ACTIONS
        ============================================== */

        .student-table-actions {
          display: flex;
          align-items: center;
          justify-content: center;

          gap: 5px;
        }

        .table-action {
          min-height: 33px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 4px;

          padding: 6px 8px;

          border-radius: 6px;

          font-size: 10px;
          font-weight: 700;

          cursor: pointer;
        }

        .table-action.present {
          border: 1px solid #cce9d8;

          background: #edf9f2;
          color: #278b53;
        }

        .table-action.absent {
          border: 1px solid #f0cece;

          background: #fff3f3;
          color: #cf4647;
        }

        .table-action.punch {
          border: 1px solid #d8d9ff;

          background: #f2f2ff;
          color: #696cff;
        }

        .table-action.punch-out {
          border: 1px solid #f0dfbd;

          background: #fff7e8;
          color: #ba791d;
        }

        .attendance-complete {
          display: inline-flex;
          align-items: center;

          gap: 4px;

          color: #6e737d;

          font-size: 10px;
          font-weight: 700;
        }

        /* ==============================================
           MESSAGE BUTTON
        ============================================== */

        .message-delivery-btn {
          position: relative;

          min-height: 32px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 4px;

          padding: 6px 8px;

          border-radius: 6px;

          font-size: 10px;
          font-weight: 700;

          cursor: pointer;
        }

        .message-delivery-btn.has-delivery {
          border: 1px solid #cfe8d9;

          background: #eef9f2;
          color: #288553;
        }

        .message-delivery-btn.needs-attention {
          border: 1px solid #f0ddba;

          background: #fff8ea;
          color: #b6771c;
        }

        .delivery-dot {
          position: absolute;

          top: -3px;
          right: -3px;

          width: 8px;
          height: 8px;

          border: 2px solid #fff;
          border-radius: 50%;

          background: #d34849;
        }

        .attendance-no-data {
          height: 220px;

          text-align:
            center !important;
        }

        .attendance-no-data > i {
          display: block;

          margin-bottom: 7px;

          color: #a0a5af;

          font-size: 31px;
        }

        .attendance-no-data strong {
          display: block;

          color: #565b65;

          font-size: 13px;
        }

        .attendance-no-data span {
          display: block;

          margin-top: 4px;

          color: #999ea8;

          font-size: 10px;
        }

        .attendance-list-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 10px;

          padding: 9px 13px;

          border-top:
            1px solid #e6e9ee;

          background: #fafbfc;

          color: #8b909b;

          font-size: 10px;
        }

        .attendance-list-footer span {
          display: flex;
          align-items: center;

          gap: 4px;
        }

        .attendance-list-footer strong {
          color: #5e636e;
        }

        /* ==============================================
           MOBILE LIST
        ============================================== */

        .attendance-mobile-list {
          display: none;
        }

        /* ==============================================
           MODALS
        ============================================== */

        .attendance-modal-backdrop {
          position: fixed;

          inset: 0;

          z-index: 99999;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 20px;

          background:
            rgba(24,28,38,.55);

          backdrop-filter:
            blur(2px);
        }

        .attendance-modal {
          width: 100%;
          max-width: 510px;

          max-height: 92vh;

          overflow: auto;

          border-radius: 14px;

          background: #fff;

          box-shadow:
            0 20px 60px
            rgba(20,25,40,.25);
        }

        .message-report-modal {
          max-width: 850px;
        }

        .attendance-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          padding: 16px 18px;

          border-bottom:
            1px solid #e7e9ee;
        }

        .attendance-modal-header h3 {
          display: flex;
          align-items: center;

          gap: 6px;

          margin: 0;

          color: #40454f;

          font-size: 17px;
        }

        .attendance-modal-header h3 i {
          color: #696cff;
        }

        .attendance-modal-header p {
          margin: 4px 0 0;

          color: #969ba5;

          font-size: 10px;
        }

        .attendance-modal-header
        > button {
          width: 34px;
          height: 34px;

          display: flex;
          align-items: center;
          justify-content: center;

          border: 0;
          border-radius: 7px;

          background: #f3f4f6;
          color: #666b75;

          cursor: pointer;
        }

        .attendance-modal-body {
          padding: 18px;
        }

        .attendance-modal-footer {
          display: flex;
          justify-content: flex-end;

          gap: 7px;

          padding: 13px 18px;

          border-top:
            1px solid #e7e9ee;
        }

        .modal-student {
          display: flex;
          align-items: center;

          gap: 10px;

          padding: 12px;

          margin-bottom: 14px;

          border: 1px solid #e2e5eb;
          border-radius: 9px;

          background: #fafbfc;
        }

        .modal-student
        > div:last-child {
          display: flex;
          flex-direction: column;
        }

        .modal-student strong {
          color: #414650;

          font-size: 14px;
        }

        .modal-student span {
          margin-top: 2px;

          color: #9297a2;

          font-size: 10px;
        }

        .punch-date-info {
          display: flex;
          flex-wrap: wrap;

          gap: 6px;

          margin-bottom: 14px;
        }

        .punch-date-info span {
          display: inline-flex;
          align-items: center;

          gap: 4px;

          padding: 6px 8px;

          border-radius: 6px;

          background: #f1f2f5;
          color: #656a74;

          font-size: 10px;
          font-weight: 600;
        }

        .message-info-box,
        .message-warning-box {
          display: flex;
          align-items: flex-start;

          gap: 8px;

          padding: 10px;

          margin-top: 14px;

          border-radius: 8px;
        }

        .message-info-box {
          background: #eef6ff;
          color: #367cad;
        }

        .message-warning-box {
          background: #fff4e8;
          color: #b37319;
        }

        .message-info-box > i,
        .message-warning-box > i {
          margin-top: 1px;

          font-size: 18px;
        }

        .message-info-box div,
        .message-warning-box div {
          display: flex;
          flex-direction: column;
        }

        .message-info-box strong,
        .message-warning-box strong {
          font-size: 11px;
        }

        .message-info-box span,
        .message-warning-box span {
          margin-top: 2px;

          font-size: 10px;

          line-height: 1.4;
        }

        .modal-cancel-btn,
        .modal-primary-btn,
        .message-retry-btn {
          min-height: 39px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          gap: 5px;

          padding: 7px 12px;

          border-radius: 7px;

          font-size: 11px;
          font-weight: 700;

          cursor: pointer;
        }

        .modal-cancel-btn {
          border: 1px solid #dfe2e8;

          background: #fff;
          color: #646974;
        }

        .modal-primary-btn {
          border: 1px solid #696cff;

          background: #696cff;
          color: #fff;
        }

        .message-retry-btn {
          border: 1px solid #696cff;

          background: #696cff;
          color: #fff;
        }

        .message-delivery-btn {
          flex-wrap: wrap;
          max-width: 190px;
        }

        .delivery-main-text {
          font-weight: 800;
        }

        .delivery-channel-text {
          width: 100%;
          margin-top: 1px;
          color: inherit;
          font-size: 8px;
          font-weight: 600;
          line-height: 1.25;
          opacity: .85;
        }

        .message-delivery-btn.processing-delivery {
          border: 1px solid #cfe5f5;
          background: #eef7fd;
          color: #347fae;
        }

        .mobile-delivery-channels {
          display: block;
          width: 100%;
          margin-top: 2px;
          font-size: 8px;
          font-weight: 600;
          line-height: 1.25;
          opacity: .85;
        }

        .student-action.delivery.not-delivered {
          border-color: #f0ddba;
          background: #fff8ea;
          color: #b6771c;
        }

        .overall-delivery-box {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          margin-bottom: 12px;
          border: 1px solid #e2e5eb;
          border-radius: 9px;
        }

        .overall-delivery-box.delivered {
          border-color: #cfe8d9;
          background: #eef9f2;
          color: #278754;
        }

        .overall-delivery-box.processing {
          border-color: #cfe5f5;
          background: #eef7fd;
          color: #347fae;
        }

        .overall-delivery-box.not-delivered {
          border-color: #f0d6d6;
          background: #fff4f4;
          color: #c94849;
        }

        .overall-delivery-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 40px;
          border-radius: 9px;
          background: rgba(255,255,255,.78);
          font-size: 21px;
        }

        .overall-delivery-box > div:last-child {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .overall-delivery-box small {
          font-size: 9px;
          font-weight: 600;
          opacity: .75;
        }

        .overall-delivery-box strong {
          margin-top: 1px;
          font-size: 14px;
        }

        .overall-delivery-box span {
          margin-top: 2px;
          font-size: 9px;
          line-height: 1.4;
          opacity: .85;
        }

        .delivery-channel-chip {
          font-size: 8px;
        }

        /* ==============================================
           MESSAGE DELIVERY
        ============================================== */

        .message-delivery-list {
          overflow: hidden;

          border: 1px solid #e2e5eb;
          border-radius: 9px;
        }

        .delivery-row {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 10px;

          padding: 12px;
        }

        .delivery-row + .delivery-row {
          border-top:
            1px solid #e9ebef;
        }

        .delivery-row > div {
          display: flex;
          align-items: center;

          gap: 8px;
        }

        .delivery-row
        > div
        > div {
          display: flex;
          flex-direction: column;
        }

        .delivery-row strong {
          color: #4b505a;

          font-size: 11px;
        }

        .delivery-row small {
          margin-top: 2px;

          color: #969ba5;

          font-size: 9px;
        }

        .delivery-channel-icon {
          width: 36px;
          height: 36px;

          display: flex;
          align-items: center;
          justify-content: center;

          flex: 0 0 36px;

          border-radius: 8px;

          font-size: 17px;
        }

        .delivery-channel-icon.meta {
          background: #e8f8ef;
          color: #268a52;
        }

        .delivery-channel-icon.push {
          background: #eef0ff;
          color: #696cff;
        }

        .delivery-status {
          display: inline-flex;
          align-items: center;

          gap: 4px;

          padding: 5px 7px;

          border-radius: 6px;

          font-size: 9px;
          font-weight: 700;

          white-space: nowrap;
        }

        .message-sent {
          background: #e8f8ef;
          color: #268a52;
        }

        .message-processing {
          background: #eaf6ff;
          color: #3583ba;
        }

        .message-failed {
          background: #ffeded;
          color: #d34849;
        }

        .message-not-sent {
          background: #fff3e2;
          color: #bd751a;
        }

        .message-punch-status {
          display: grid;

          grid-template-columns:
            repeat(2,1fr);

          gap: 7px;

          margin-top: 10px;
        }

        .message-punch-status > div {
          display: flex;
          align-items: center;
          justify-content: space-between;

          padding: 8px;

          border: 1px solid #e5e7ec;
          border-radius: 7px;
        }

        .message-punch-status span {
          color: #858a94;

          font-size: 9px;
        }

        .message-punch-status strong {
          padding: 4px 6px;

          border-radius: 5px;

          font-size: 9px;
        }

        /* ==============================================
           MESSAGE HISTORY
        ============================================== */

        .message-history-modal {
          max-width: 650px;
        }

        .overall-delivery-box.failed {
          border-color: #f1cece;
          background: #fff1f1;
          color: #c94344;
        }

        .message-history-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin: 17px 0 9px;
        }

        .message-history-heading > div {
          display: flex;
          flex-direction: column;
        }

        .message-history-heading strong {
          color: #444953;
          font-size: 12px;
        }

        .message-history-heading span:not(.history-count) {
          margin-top: 2px;
          color: #959aa5;
          font-size: 9px;
        }

        .history-count {
          min-width: 24px;
          height: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0 7px;
          border-radius: 20px;
          background: #eef0ff;
          color: #696cff;
          font-size: 10px;
          font-weight: 800;
        }

        .message-history-list {
          display: flex;
          flex-direction: column;
          gap: 9px;
        }

        .message-history-event {
          overflow: hidden;
          border: 1px solid #e1e4ea;
          border-radius: 10px;
          background: #fff;
        }

        .history-event-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 11px 12px;
          border-bottom: 1px solid #eceef2;
          background: #fafbfc;
        }

        .history-event-header > div {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .history-event-header > div > div {
          display: flex;
          flex-direction: column;
        }

        .history-event-header strong {
          color: #484d57;
          font-size: 11px;
        }

        .history-event-header small {
          margin-top: 1px;
          color: #969ba5;
          font-size: 9px;
        }

        .history-event-icon {
          width: 31px;
          height: 31px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 31px;
          border-radius: 7px;
          font-size: 15px;
        }

        .history-event-icon.in {
          background: #e8f8ef;
          color: #278b53;
        }

        .history-event-icon.out {
          background: #fff3e2;
          color: #ba791d;
        }

        .history-event-icon.general {
          background: #eef0ff;
          color: #696cff;
        }

        .history-overall-status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 7px;
          border-radius: 6px;
          font-size: 9px;
          font-weight: 800;
          white-space: nowrap;
        }

        .history-overall-status.delivered {
          background: #e8f8ef;
          color: #278b53;
        }

        .history-overall-status.processing {
          background: #eaf6ff;
          color: #3583ba;
        }

        .history-overall-status.failed {
          background: #ffeded;
          color: #d34849;
        }

        .history-overall-status.not-delivered {
          background: #fff3e2;
          color: #bd751a;
        }

        .history-channel-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          padding: 10px 12px;
        }

        .history-channel {
          display: grid;
          grid-template-columns: 34px minmax(0, 1fr) 18px;
          align-items: center;
          gap: 7px;
          padding: 9px;
          border: 1px solid #e5e7ec;
          border-radius: 8px;
          background: #fff;
        }

        .history-channel.delivered {
          border-color: #cfe8d9;
          background: #f4fbf7;
          color: #278754;
        }

        .history-channel.processing {
          border-color: #d1e7f6;
          background: #f4faff;
          color: #347fae;
        }

        .history-channel.failed {
          border-color: #f0d0d0;
          background: #fff5f5;
          color: #cc4849;
        }

        .history-channel.not-delivered {
          border-color: #eedfca;
          background: #fffbf4;
          color: #b7771d;
        }

        .history-channel-icon {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 7px;
          font-size: 16px;
        }

        .history-channel-icon.whatsapp {
          background: #e6f7ee;
          color: #258b52;
        }

        .history-channel-icon.push {
          background: #eef0ff;
          color: #696cff;
        }

        .history-channel > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .history-channel small {
          color: #9297a2;
          font-size: 8px;
        }

        .history-channel strong {
          margin-top: 1px;
          color: currentColor;
          font-size: 10px;
        }

        .history-channel > i {
          font-size: 16px;
        }

        .history-delivered-via {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 12px;
          border-top: 1px solid #dcefe4;
          background: #eef9f2;
          color: #278754;
          font-size: 9px;
          line-height: 1.4;
        }

        .history-delivered-via > i {
          font-size: 14px;
        }

        .message-history-empty {
          min-height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 20px;
          border: 1px dashed #d8dbe2;
          border-radius: 9px;
          color: #9297a2;
          text-align: center;
        }

        .message-history-empty > i {
          margin-bottom: 5px;
          font-size: 26px;
        }

        .message-history-empty strong {
          color: #60656f;
          font-size: 11px;
        }

        .message-history-empty span {
          margin-top: 3px;
          font-size: 9px;
        }

        .general-message-status {
          display: grid;
          grid-template-columns: minmax(0,1fr) auto;
          gap: 8px 10px;
          align-items: center;
          padding: 10px;
          margin-top: 10px;
          border: 1px solid #e2e5eb;
          border-radius: 9px;
        }

        .general-message-status > div {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .general-message-status > div > div {
          display: flex;
          flex-direction: column;
        }

        .general-message-status strong {
          color: #4c515b;
          font-size: 10px;
        }

        .general-message-status small {
          color: #969ba5;
          font-size: 8px;
        }

        @media (max-width: 575px) {
          .history-channel-grid {
            grid-template-columns: 1fr;
          }

          .history-event-header {
            align-items: flex-start;
          }

          .history-overall-status {
            margin-top: 2px;
          }

          .general-message-status {
            grid-template-columns: 1fr auto;
          }
        }

        /* ==============================================
           MESSAGE REPORT
        ============================================== */

        .message-report-grid {
          display: grid;

          grid-template-columns:
            repeat(2,1fr);

          gap: 10px;
        }

        .message-report-channel {
          padding: 13px;

          border: 1px solid #e1e4ea;
          border-radius: 9px;
        }

        .message-channel-title {
          display: flex;
          align-items: center;

          gap: 8px;

          margin-bottom: 11px;
        }

        .message-channel-title
        > div:last-child {
          display: flex;
          flex-direction: column;
        }

        .message-channel-title strong {
          color: #484d57;

          font-size: 12px;
        }

        .message-channel-title small {
          color: #979ca6;

          font-size: 9px;
        }

        .message-count-grid {
          display: grid;

          grid-template-columns:
            repeat(3,1fr);

          gap: 6px;
        }

        .message-count {
          display: flex;
          flex-direction: column;

          padding: 9px;

          border: 0;
          border-radius: 7px;

          text-align: left;
        }

        button.message-count {
          cursor: pointer;
        }

        .message-count small {
          font-size: 8px;
          font-weight: 600;
        }

        .message-count strong {
          margin-top: 2px;

          font-size: 17px;
        }

        .message-count.sent {
          background: #eaf8ef;
          color: #278753;
        }

        .message-count.pending {
          background: #fff3e2;
          color: #bd761b;
        }

        .message-count.processing {
          background: #eaf6ff;
          color: #3785bb;
        }

        .message-report-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 10px;

          margin-top: 16px;
          margin-bottom: 7px;
        }

        .message-report-list-header
        > div {
          display: flex;
          align-items: center;

          gap: 6px;
        }

        .message-report-list-header strong {
          color: #4c515b;

          font-size: 12px;
        }

        .message-report-list-header span {
          color: #969ba5;

          font-size: 9px;
        }

        .message-report-list-header button {
          display: inline-flex;
          align-items: center;

          gap: 3px;

          padding: 5px 7px;

          border: 1px solid #e1e4e9;
          border-radius: 5px;

          background: #fff;
          color: #666b75;

          font-size: 9px;

          cursor: pointer;
        }

        .message-student-list {
          overflow: auto;

          max-height: 330px;

          border: 1px solid #e1e4e9;
          border-radius: 8px;
        }

        .message-student-row {
          width: 100%;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 12px;

          padding: 9px 11px;

          border: 0;
          border-bottom:
            1px solid #eceef2;

          background: #fff;

          text-align: left;

          cursor: pointer;
        }

        .message-student-row:last-child {
          border-bottom: 0;
        }

        .message-student-row:hover {
          background: #fafbfc;
        }

        .message-row-statuses {
          display: flex;
          align-items: center;

          gap: 5px;
        }

        .row-arrow {
          color: #a2a7b0;

          font-size: 18px;
        }

        .message-report-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          min-height: 160px;

          color: #9297a2;
        }

        .message-report-empty > i {
          margin-bottom: 5px;

          font-size: 27px;
        }

        .message-report-empty strong {
          color: #606570;

          font-size: 11px;
        }

        .message-report-empty span {
          margin-top: 3px;

          font-size: 9px;
        }

        /* ==============================================
           LOADING
        ============================================== */

        .attendance-spin {
          display: inline-block;

          animation:
            attendanceSpin 1s
            linear infinite;
        }

        @keyframes attendanceSpin {
          to {
            transform:
              rotate(360deg);
          }
        }

        /* ==============================================
           RESPONSIVE
        ============================================== */

        @media
        (max-width: 1300px) {

          .attendance-filter-grid {
            grid-template-columns:
              repeat(3,1fr);
          }

        }

        @media
        (max-width: 1000px) {

          .attendance-page-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .attendance-header-actions {
            width: 100%;
          }

          .attendance-mode-bar {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .attendance-summary {
            grid-template-columns:
              repeat(2,1fr);
          }

          .attendance-list-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .attendance-list-actions {
            width: 100%;
          }

          .attendance-search {
            flex: 1;
          }

          .attendance-search input {
            width: 100%;
          }

        }

        @media
        (max-width: 767px) {

          .attendance-manual-content {
            padding: 10px;
          }

          .attendance-page-header {
            padding: 14px;
          }

          .attendance-title-icon {
            display: none;
          }

          .attendance-page-header h1 {
            font-size: 20px;
          }

          .attendance-page-header p {
            font-size: 11px;
          }

          .attendance-header-actions {
            display: grid;

            grid-template-columns:
              repeat(3,1fr);

            gap: 5px;
          }

          .attendance-header-btn {
            width: 100%;

            padding: 7px 5px;

            font-size: 10px;
          }

          .attendance-filter-card {
            padding: 13px;
          }

          .attendance-filter-grid {
            grid-template-columns:
              1fr;
          }

          .attendance-field label {
            font-size: 12px;
          }

          .attendance-input select,
          .attendance-input input {
            height: 46px;

            font-size: 13px;
          }

          .attendance-summary {
            gap: 6px;
          }

          .attendance-summary-card {
            padding: 10px;
          }

          .summary-card-icon {
            width: 34px;
            height: 34px;

            flex-basis: 34px;

            font-size: 16px;
          }

          .attendance-summary-card strong {
            font-size: 19px;
          }

          .attendance-summary-card small {
            font-size: 9px;
          }

          .attendance-list-actions {
            align-items: stretch;

            flex-direction:
              column;
          }

          .mark-all-absent-btn,
          .attendance-search,
          .attendance-search input {
            width: 100%;
          }

          /* DESKTOP TABLE OFF */

          .attendance-table-scroll {
            display: none;
          }

          /* MOBILE CARDS ON */

          .attendance-mobile-list {
            display: block;

            padding: 8px;
          }

          .attendance-mobile-student {
            padding: 11px;

            margin-bottom: 7px;

            border: 1px solid #e3e6eb;
            border-radius: 9px;

            background: #fff;
          }

          .attendance-mobile-student:last-child {
            margin-bottom: 0;
          }

          .mobile-student-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;

            gap: 8px;
          }

          .mobile-time-row {
            display: flex;

            gap: 15px;

            padding: 7px 0;

            margin-top: 7px;

            border-top:
              1px solid #eceef2;

            color: #8a8f99;

            font-size: 9px;
          }

          .mobile-time-row span {
            display: flex;
            align-items: center;

            gap: 4px;
          }

          .mobile-time-row strong {
            color: #555a64;
          }

          .mobile-attendance-actions {
            display: flex;
            flex-wrap: wrap;

            gap: 5px;

            padding-top: 8px;

            margin-top: 8px;

            border-top:
              1px solid #eceef2;
          }

          .student-action {
            min-height: 35px;

            display: inline-flex;
            align-items: center;
            justify-content: center;

            gap: 4px;

            flex: 1;

            padding: 6px 8px;

            border-radius: 6px;

            font-size: 10px;
            font-weight: 700;
          }

          .student-action.present {
            border: 1px solid #cde8d7;

            background: #edf9f2;
            color: #278b53;
          }

          .student-action.absent {
            border: 1px solid #f0cece;

            background: #fff3f3;
            color: #cf4647;
          }

          .student-action.punch {
            border: 1px solid #d8d9ff;

            background: #f2f2ff;
            color: #696cff;
          }

          .student-action.punch-out {
            border: 1px solid #f0dfbd;

            background: #fff7e8;
            color: #ba791d;
          }

          .student-action.delivery {
            border: 1px solid #ebdfc7;

            background: #fff8eb;
            color: #b2761d;
          }

          .student-action.delivery.delivered {
            border-color: #cde8d7;

            background: #edf9f2;
            color: #278b53;
          }

          .attendance-list-footer {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .attendance-modal-backdrop {
            padding: 8px;
          }

          .attendance-modal {
            max-height: 95vh;
          }

          .message-report-grid {
            grid-template-columns:
              1fr;
          }

          .message-student-row {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .message-row-statuses {
            width: 100%;
          }

        }

        @media
        (max-width: 430px) {

          .attendance-header-actions {
            grid-template-columns:
              1fr 1fr;
          }

          .attendance-summary {
            grid-template-columns:
              repeat(2,1fr);
          }

          .attendance-current-batch {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .attendance-mode-buttons {
            width: 100%;
          }

          .attendance-mode-buttons button {
            flex: 1;

            justify-content: center;
          }

          .message-count-grid {
            grid-template-columns:
              repeat(3,1fr);
          }

        }


        /* ==============================================
           APPLICATION SHELL RESPONSIVE FIXES
        ============================================== */

        @media (max-width: 991.98px) {
          .attendance-manual-container {
            padding: 12px;
          }

          .attendance-master-wrapper {
            margin-bottom: 12px;
          }

          .attendance-manual-content {
            width: 100%;
            min-width: 0;
            padding: 0;
          }
        }

        @media (max-width: 575.98px) {
          .attendance-manual-container {
            padding: 10px;
          }

          .attendance-master-wrapper {
            margin-bottom: 10px;
          }

          .attendance-manual-content {
            padding: 0;
          }
        }

      
        /* SCAI ATTENDANCE: flush navbar fix, isolated to this page.
           The global template otherwise adds padding above .layout-page. */
        html.layout-navbar-fixed .layout-wrapper.attendance-manual-page:not(.layout-without-menu) > .layout-container > .layout-page,
        html.layout-navbar-fixed .layout-wrapper.attendance-manual-page .layout-page,
        .layout-wrapper.attendance-manual-page > .layout-container > .layout-page {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.attendance-manual-page > .layout-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.attendance-manual-page .layout-page > .no-print:first-child {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        .layout-wrapper.attendance-manual-page #layout-navbar.scai-topbar {
          top: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          max-width: none !important;
          border-radius: 0 !important;
        }
`}</style>
    </>
  );
}
